import { abilitiesForRarity } from "./heroAbilities";

export const COMBAT_STAT_KEYS = ["hp", "atk", "def", "spd", "crit"];

export const COMBAT_STAT_FIELDS = [
  { key: "hp", label: "HP" },
  { key: "atk", label: "ATK" },
  { key: "def", label: "DEF" },
  { key: "spd", label: "SPD" },
  { key: "crit", label: "CRIT", suffix: "%" },
];

export const HERO_ABILITY_SLOTS = [
  { key: "normal", label: "Normal" },
  { key: "special_1", label: "Special 1" },
  { key: "special_2", label: "Special 2" },
  { key: "ultimate", label: "Ultimate" },
];

const SKIP_BONUS_KEYS = new Set(["stats", "abilities"]);

export function emptyCombatStats() {
  return { hp: 0, atk: 0, def: 0, spd: 0, crit: 0 };
}

export function addCombatStats(target, source) {
  const out = target || emptyCombatStats();
  for (const key of COMBAT_STAT_KEYS) {
    const n = Number(source?.[key]);
    if (Number.isFinite(n)) out[key] += n;
  }
  return out;
}

export function mergeExtras(target, source) {
  const out = target || {};
  if (!source || typeof source !== "object") return out;
  for (const [key, value] of Object.entries(source)) {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) continue;
    out[key] = (out[key] || 0) + n;
  }
  return out;
}

export function formatCombatValue(key, amount, { signed = false } = {}) {
  const n = Number(amount);
  const value = Number.isFinite(n) ? n : 0;
  const suffix = key === "crit" ? "%" : "";
  const sign = signed && value > 0 ? "+" : "";
  return `${sign}${value}${suffix}`;
}

export function compactStatSummary(stats) {
  return COMBAT_STAT_FIELDS.filter((field) => {
    const n = Number(stats?.[field.key]);
    return Number.isFinite(n) && n !== 0;
  })
    .map(
      (field) =>
        `${formatCombatValue(field.key, stats[field.key], { signed: true })} ${field.label}`
    )
    .join(" · ");
}

export function titleCaseStatKey(key) {
  const text = String(key || "").replace(/[_-]+/g, " ").trim();
  if (!text) return "";
  return text.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function isHeroRoot(node) {
  const key = node?.node_key || node?.id;
  return key === "Hero";
}

function isEquipmentSocket(node) {
  if (!node || isHeroRoot(node)) return false;
  return Boolean(node.socket_type);
}

function nodeLabel(node, raceLabelKey) {
  const labels = node?.labels;
  if (labels && typeof labels === "object") {
    const raced = raceLabelKey ? labels[raceLabelKey] : null;
    if (raced?.label) return raced.label;
    for (const entry of Object.values(labels)) {
      if (entry?.label) return entry.label;
    }
  }
  return node?.node_key || node?.id || "";
}

function ingestNumericMap(stats, extras, map, skipKeys) {
  if (!map || typeof map !== "object") return;
  for (const [key, value] of Object.entries(map)) {
    if (skipKeys?.has(key)) continue;
    if (value && typeof value === "object") continue;
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) continue;
    if (COMBAT_STAT_KEYS.includes(key)) {
      stats[key] += n;
    } else {
      extras[key] = (Number(extras[key]) || 0) + n;
    }
  }
}

function extractAbilities(abilities) {
  if (!abilities || typeof abilities !== "object") return [];
  if (Array.isArray(abilities)) {
    return abilities
      .filter((ability) => ability && (ability.name || ability.key))
      .map((ability, index) => ({
        key: ability.key || ability.name || `ability-${index}`,
        label: ability.label || "",
        name: ability.name || String(ability.key || "").replace(/_/g, " "),
        description: ability.description || ability.effect || "",
      }));
  }
  return HERO_ABILITY_SLOTS.map((slot) => {
    const ability = abilities[slot.key] || {};
    return {
      key: slot.key,
      label: slot.label,
      name: ability.name || "",
      description: ability.description || "",
    };
  }).filter((item) => item.name);
}

function extractAffixes(card) {
  const instance = card?.instance_data;
  const instanceAffixes = instance?.affixes;
  const list = Array.isArray(instanceAffixes)
    ? instanceAffixes
    : Array.isArray(card?.affixes)
      ? card.affixes
      : [];
  const out = [];
  for (const affix of list) {
    if (!affix) continue;
    const name =
      affix.name || (affix.key ? String(affix.key).replace(/_/g, " ") : "");
    if (!name) continue;
    out.push({
      name,
      effect: affix.effect || affix.description || "",
    });
  }
  return out;
}

function readCardStatsAndExtras(card) {
  const stats = emptyCombatStats();
  const extras = {};
  if (!card) return { stats, extras };
  const instance = card.instance_data;
  const instanceStats = instance && instance.stats;
  const flattened = card.stats;
  const source =
    instanceStats && typeof instanceStats === "object"
      ? instanceStats
      : flattened && typeof flattened === "object"
        ? flattened
        : {};
  for (const key of COMBAT_STAT_KEYS) {
    const n = Number(source[key]);
    stats[key] = Number.isFinite(n) ? n : 0;
  }
  if (card.type === "hero") {
    const instanceSpd = Number(instanceStats?.spd);
    const catalogSpd = Number(flattened?.spd);
    stats.spd = Math.max(
      Number.isFinite(instanceSpd) ? instanceSpd : 0,
      Number.isFinite(catalogSpd) ? catalogSpd : 0
    );
  }
  ingestNumericMap(stats, extras, source, new Set(COMBAT_STAT_KEYS));
  return { stats, extras };
}

function extractBonus(bonus) {
  const stats = emptyCombatStats();
  const extras = {};
  if (!bonus || typeof bonus !== "object") {
    return { stats, extras, abilities: [] };
  }
  const abilities = extractAbilities(bonus.abilities);
  ingestNumericMap(stats, extras, bonus, SKIP_BONUS_KEYS);
  if (bonus.stats && typeof bonus.stats === "object") {
    ingestNumericMap(stats, extras, bonus.stats, new Set());
  }
  return { stats, extras, abilities };
}

export function buildHeroCombatSheet({
  hero,
  heroCard,
  ownedCards,
  heroSockets,
  nodes,
  raceLabelKey,
} = {}) {
  const identity = {
    name: hero?.name || "Unknown Hero",
    level: hero?.level || 1,
    experience: hero?.experience || 0,
    card: heroCard || null,
  };

  const baseRead = readCardStatsAndExtras(heroCard);
  const base = {
    stats: baseRead.stats,
    extras: baseRead.extras,
    abilities: extractAbilities(
      abilitiesForRarity(
        heroCard?.abilities,
        heroCard?.rarity || heroCard?.instance_data?.rarity
      )
    ),
  };

  const equipmentStats = emptyCombatStats();
  const equipmentExtras = {};
  const affixes = [];
  const pieces = [];
  const byId = Object.fromEntries(
    (ownedCards || []).map((card) => [card.id, card])
  );
  const nodeByKey = Object.fromEntries(
    (nodes || []).map((node) => [node.node_key || node.id, node])
  );

  for (const socket of heroSockets || []) {
    const nodeKey = socket?.node_key;
    if (!nodeKey || nodeKey === "Hero") continue;
    const node = nodeByKey[nodeKey];
    if (node && !isEquipmentSocket(node)) continue;
    const card = byId[socket.player_card_id];
    if (!card) continue;
    const read = readCardStatsAndExtras(card);
    addCombatStats(equipmentStats, read.stats);
    mergeExtras(equipmentExtras, read.extras);
    const pieceAffixes = extractAffixes(card);
    affixes.push(...pieceAffixes);
    const slot = node?.socket_type || card.sub_type || nodeKey;
    pieces.push({
      nodeKey,
      slot,
      label: nodeLabel(node, raceLabelKey) || card.name,
      card,
      stats: read.stats,
      extras: read.extras,
      affixes: pieceAffixes,
    });
  }

  const passiveStats = emptyCombatStats();
  const passiveExtras = {};
  const passiveAbilities = [];
  const passiveNodes = [];

  for (const node of nodes || []) {
    if (!node?.isUnlocked) continue;
    if (isHeroRoot(node) || isEquipmentSocket(node)) continue;
    const extracted = extractBonus(node.bonus);
    addCombatStats(passiveStats, extracted.stats);
    mergeExtras(passiveExtras, extracted.extras);
    passiveAbilities.push(...extracted.abilities);
    passiveNodes.push({
      nodeKey: node.node_key || node.id,
      label: nodeLabel(node, raceLabelKey),
      stats: extracted.stats,
      extras: extracted.extras,
      abilities: extracted.abilities,
    });
  }

  const totalStats = emptyCombatStats();
  addCombatStats(totalStats, base.stats);
  addCombatStats(totalStats, equipmentStats);
  addCombatStats(totalStats, passiveStats);
  const totalExtras = {};
  mergeExtras(totalExtras, base.extras);
  mergeExtras(totalExtras, equipmentExtras);
  mergeExtras(totalExtras, passiveExtras);

  return {
    identity,
    base,
    equipment: {
      stats: equipmentStats,
      extras: equipmentExtras,
      affixes,
      pieces,
    },
    passives: {
      stats: passiveStats,
      extras: passiveExtras,
      abilities: passiveAbilities,
      nodes: passiveNodes,
    },
    total: {
      stats: totalStats,
      extras: totalExtras,
    },
  };
}
